import { useCallback, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import AllTransactions from './components/AllTransactions';
import {
  fetchAndSetTransactionMetrics,
  goBackToPlatformSelect,
} from './EdoTransactions.helpers';
import SelectPlatform from 'components/global/SelectPlatform/SelectPlatform';
import TransactionSignal from 'signals/Transaction.signal';
import BackButton from 'components/global/BackButton';
import TestModeBanner from '../../../global/utils/TestModeBanner';
import { checkEdoPermissionsAcrossPrograms } from '../../../../libs/edoPermissions';
import userAccountSignal from '../../../../signals/UserAccount.signal';
import api from '../../../../api/api';
import loaderSignal from '../../../../signals/Loader.signal';

const Transactions = () => {
  const { form } = TransactionSignal.value;
  const [canView, setCanView] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check if user has canView permission on any program
  useEffect(() => {
    const checkViewPermission = async () => {
      try {
        setIsChecking(true);
        loaderSignal.update({ isContentLoading: true });
        const userData = userAccountSignal.value.userData;
        
        // Wait for userData to be loaded
        if (!userData) {
          setIsChecking(false);
          loaderSignal.reset();
          return;
        }

        // Fetch funding programs for the EDO
        const fundingPrograms = await api.get({
          path: '/fundingPrograms',
          options: {
            where: {
              edoId: userData?.account.edoId,
            },
          },
        });

        const permissions = await checkEdoPermissionsAcrossPrograms(
          fundingPrograms,
          ['canView']
        );
        setCanView(permissions.canView);
      } catch (error) {
        console.error('Error checking view permission:', error);
        setCanView(false);
      } finally {
        setIsChecking(false);
        loaderSignal.reset();
      }
    };

    checkViewPermission();
  }, [userAccountSignal.value.userData]);

  const init = useCallback(async () => {
    if (canView) {
      await fetchAndSetTransactionMetrics();
    }
  }, [canView]);

  useEffect(() => {
    init();
  }, [init]);

  if (isChecking) {
    return (
      <Container fluid>
        <Row className="justify-content-center border border-gray py-24 mb-32 bg-white shadow-sm">
          <Col>
            <p className="text-center">Loading...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!canView) {
    return (
      <Container fluid>
        <Row className="justify-content-center border border-gray py-24 mb-32 bg-white shadow-sm">
          <Col>
            <h3 className="text-center">Access Denied</h3>
            <p className="text-center">
              You do not have permission to view transactions.
            </p>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <BackButton
        buttonClassName="mb-8"
        onClick={goBackToPlatformSelect}
        buttonText="Back to Select Platform"
        hidden={form === 'selectPlatform'}
      />
      <Row className="justify-content-center border border-gray py-24 mb-32 bg-white shadow-sm">
        <Col>
          <h3
            className={
              form === 'selectPlatform' ? 'mb-24 text-center' : 'text-center'
            }
          >
            Transactions
          </h3>
          <hr />
          {form === 'selectPlatform' ? (
            <SelectPlatform
              signal={TransactionSignal}
              nextForm="transactions"
            />
          ) : (
            <div className="bg-white">
              <TestModeBanner />
              <AllTransactions />
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default Transactions;
